import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate, useParams } from 'react-router';
import LoadingButton from '../Shared/Loading/index.tsx';
import { LoginActions } from '../../Services/_actions/LoginAction/LoginAction.ts';
import { validatePassword } from '../Management/validations.ts';

const passwordRegex =  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{12,}$/;

// TypeScript interface for password validation state
/**
 * @typedef {Object} PasswordValidationState
 * @property {boolean} minLength
 * @property {boolean} uppercase
 * @property {boolean} lowercase
 * @property {boolean} number
 * @property {boolean} specialChar
 */

const ResetPassword = () => {
    const actions = LoginActions();
    const navigate = useNavigate();
    const { code, email } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        noCommonWords: true,
        noUsernameSubstring: true,
    });
    const [error, setError] = useState<string | null>(null);

    const usernameHint = decodeURIComponent(email || '').split('@')[0] || '';

    const handleSubmit = async() => {
        setIsLoading(true);
        try {
            const passwordCheck = validatePassword(password, usernameHint);
            if (!passwordCheck.isValid) {
                setError('Password must meet all password requirements shown below.');
            } else if (!passwordRegex.test(password)) {
                setError('Password must be at least 12 characters long and include uppercase, lowercase, number, and special character.');
            } else if (password !== confirmPassword) {
                setError('Passwords do not match.');
            } else {
                setError(null);
                if (!code || !email) {
                    setError('Invalid reset link. Please try again.');
                    return;
                }
                const res = await actions.resetPassword({
                    code: code,
                    email: email,
                    password: password,
                });
                if (res?.status === 200) {
                    setError(null);
                    setPassword('');
                    setConfirmPassword('');
                    setPasswordValidation({
                        minLength: false,
                        uppercase: false,
                        lowercase: false,
                        number: false,
                        specialChar: false,
                        noCommonWords: true,
                        noUsernameSubstring: true,
                    });
                    navigate('/');
                } else {
                    console.log(res);
                    setError(res?.data?.data?.general_error?.toString() ?? 'Failed to reset password.');
                }

            }
        } catch (error: any) {
    console.log('Reset Password Error:', error);

    const backendError =
        error?.response?.data?.data?.general_error ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred while resetting the password.';

    setError(backendError);
      }finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        const { rules } = validatePassword(newPassword, usernameHint);
        setPasswordValidation(rules);
    };

    useEffect(() => {
        localStorage.clear();
    }, []);
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            bgcolor="#f5f5f5"
        >
            <Card sx={{ width: 400 }}>
                <CardContent>
                    <Typography variant="h5" textAlign="center" mb={2}>
                        Reset Password
                    </Typography>
                    <TextField
                        label="New Password"
                        fullWidth
                        size='small'
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={handlePasswordChange}
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box mt={1} mb={1}>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.minLength ? 'success.main' : 'error.main'}>
                            {passwordValidation.minLength ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            At least 12 characters
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.uppercase ? 'success.main' : 'error.main'}>
                            {passwordValidation.uppercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            At least one uppercase letter (A-Z)
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.lowercase ? 'success.main' : 'error.main'}>
                            {passwordValidation.lowercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            At least one lowercase letter (a-z)
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.number ? 'success.main' : 'error.main'}>
                            {passwordValidation.number ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            At least one number (0-9)
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.specialChar ? 'success.main' : 'error.main'}>
                            {passwordValidation.specialChar ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            At least one special character
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.noCommonWords ? 'success.main' : 'error.main'}>
                            {passwordValidation.noCommonWords ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            Must not include common words (e.g. password, admin)
                        </Typography>
                        <Typography variant="caption" display="flex" alignItems="center" color={passwordValidation.noUsernameSubstring ? 'success.main' : 'error.main'}>
                            {passwordValidation.noUsernameSubstring ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                            Must not contain your username prefix (first 4–5 letters)
                        </Typography>
                    </Box>

                    <TextField
                        label="Confirm Password"
                        fullWidth
                        size='small'
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        margin="normal"
                    // Note: Password restrictions (common words, username substring) and history policy
                    // cannot be fully enforced client-side and require backend validation.
                    // Password expiry is a policy, not a validation rule for setting the password.

                    />

                    {error && (
                        <Typography variant="body2" color="error" mt={1}>
                            {error}
                        </Typography>
                    )}

                    <LoadingButton
                        buttonName='Reset Password'
                        isLoading={isLoading}
                        onClickButton={handleSubmit}
                        fullWidth
                        size='small'
                        variant="contained"
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default ResetPassword;
